import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { StatusPill } from "./PlatformOnboarding";
import { CustomerAutomationsPanel } from "./CustomerAutomationsPanel";
import { GoLiveReadinessPanel } from "./GoLiveReadinessPanel";
import {
  ArrowLeft, Building2, CheckCircle2, Circle, CircleDot, User2, Palette, Workflow,
  Boxes, Save, ExternalLink, Users2, UserPlus, Trash2, ShieldCheck, Smartphone, AlertTriangle, X,
  Database, Upload, CheckCircle, Megaphone, Mail, Bell, Smartphone as Phone, MessageSquare, Send, Rocket,
} from "lucide-react";

const STEP_ICONS = { profile: User2, branding: Palette, operating_model: Workflow, modules: Boxes, users: Users2, data: Database, communications: Megaphone, customer_automations: Send, go_live: Rocket };
const STEP_STATE_ICON = { complete: CheckCircle2, in_progress: CircleDot, not_started: Circle };
const STEP_STATE_TONE = {
  complete: "text-emerald-500", in_progress: "text-amber-500", not_started: "text-slate-300",
};

const inputCls =
  "w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400";
const sel =
  "appearance-none rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

function LabeledInput({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <input value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testId} className={inputCls} />
    </div>
  );
}

export default function PlatformOnboardingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [meta, setMeta] = useState(null);
  const [active, setActive] = useState("profile");
  const [cfg, setCfg] = useState({});
  const [brand, setBrand] = useState({});
  const [modules, setModules] = useState([]);
  const [planned, setPlanned] = useState([]);
  const [opModel, setOpModel] = useState("");
  const [saving, setSaving] = useState(false);

  const hydrate = (c) => {
    setCompany(c);
    setCfg(c.config || {});
    setBrand(c.branding || {});
    setModules(c.enabled_modules || []);
    setPlanned((c.config || {}).planned_modules || []);
    setOpModel((c.config || {}).operating_model || "");
  };

  const load = () => api.get(`/platform/companies/${id}`).then((r) => hydrate(r.data.company)).catch(() => toast.error("Company not found"));
  useEffect(() => {
    load();
    api.get("/platform/meta").then((r) => setMeta(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!company) return <div className="min-h-screen bg-slate-50" data-testid="onboarding-detail-loading" />;

  const steps = company.onboarding_steps || [];
  const stepStatus = (k) => (steps.find((s) => s.key === k) || {}).status || "not_started";

  const afterSave = (c) => { hydrate(c); toast.success("Saved"); };
  const saveConfig = async (patch) => {
    setSaving(true);
    try {
      const r = await api.put(`/platform/companies/${id}/config`, patch);
      afterSave(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  const saveProfile = () => saveConfig({
    legal_name: cfg.legal_name, trading_name: cfg.trading_name, company_number: cfg.company_number,
    vat_number: cfg.vat_number, address: cfg.address, telephone: cfg.telephone,
    general_email: cfg.general_email, website: cfg.website, timezone: cfg.timezone,
    locale: cfg.locale, currency: cfg.currency,
  });
  const saveBranding = async () => {
    setSaving(true);
    try {
      await api.put(`/platform/companies/${id}/branding`, {
        display_name: brand.display_name, logo_url: brand.logo_url,
        primary_color: brand.primary_color, accent_color: brand.accent_color,
      });
      await load(); toast.success("Saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  const saveOpModel = () => saveConfig({ operating_model: opModel });
  const saveModules = async (nextEnabled, nextPlanned) => {
    setSaving(true);
    try {
      await api.patch(`/platform/companies/${id}`, { enabled_modules: nextEnabled });
      const r = await api.put(`/platform/companies/${id}/config`, { planned_modules: nextPlanned });
      afterSave(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  const setLifecycle = async (st) => {
    try {
      const r = await api.patch(`/platform/companies/${id}/onboarding`, { onboarding_status: st });
      hydrate(r.data); toast.success(`Status → ${r.data.onboarding_status_label}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const recommended = (meta?.operating_model_modules || {})[opModel] || [];
  const applyRecommendation = () => {
    const merged = Array.from(new Set([...modules, ...recommended]));
    saveModules(merged, planned.filter((m) => !merged.includes(m)));
    toast.message("Recommended modules enabled — adjust any below.");
  };

  const toggleModule = (m, state) => {
    let e = modules.filter((x) => x !== m);
    let p = planned.filter((x) => x !== m);
    if (state === "enabled") e = [...e, m];
    if (state === "planned") p = [...p, m];
    setModules(e); setPlanned(p);
    saveModules(e, p);
  };

  const pct = company.setup_pct;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-900" data-testid="onboarding-detail-page">
      <div className="p-6 lg:p-10 max-w-6xl mx-auto">
        <button onClick={() => navigate("/platform/onboarding")} data-testid="onboarding-back" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-5">
          <ArrowLeft className="w-4 h-4" /> Client Onboarding
        </button>

        {/* Header */}
        <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-6 mb-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <span className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 font-bold uppercase">{(company.name || "?").slice(0, 2)}</span>
              <div>
                <h1 className="text-xl font-bold tracking-tight">{company.name}</h1>
                <div className="text-sm text-slate-400">/{company.slug} · {company.plan} plan · {company.member_count ?? 0} members</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <StatusPill company={company} />
              <button onClick={() => navigate(`/platform/onboarding/${company.id}/integrations`)} data-testid="integrations-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800">
                <ExternalLink className="w-4 h-4" /> Integrations
              </button>
              <button onClick={() => navigate(`/platform/companies/${company.id}`)} data-testid="open-company-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800">
                <ExternalLink className="w-4 h-4" /> Open Company
              </button>
            </div>
          </div>
          <div className="mt-5 flex items-center gap-4">
            <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full rounded-full transition-all ${pct >= 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-sm font-bold text-slate-700 tabular-nums">{pct}% complete</span>
          </div>
          {/* Lifecycle selector */}
          <div className="mt-5 flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 mr-1">Lifecycle</span>
            {(meta?.onboarding_statuses || []).map((s) => (
              <button
                key={s.key}
                onClick={() => setLifecycle(s.key)}
                disabled={company.status === "suspended"}
                data-testid={`lifecycle-${s.key}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-40 ${
                  company.onboarding_status === s.key
                    ? "bg-blue-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {s.label}
              </button>
            ))}
            {company.status === "suspended" && <span className="text-xs text-rose-500 ml-2">Company suspended — reactivate from Open Company.</span>}
          </div>
        </div>

        <div className="grid lg:grid-cols-[240px_1fr] gap-6">
          {/* Step nav */}
          <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-3 h-fit">
            {steps.map((s) => {
              const Icon = STEP_ICONS[s.key] || Circle;
              const SIcon = STEP_STATE_ICON[s.status] || Circle;
              return (
                <button
                  key={s.key}
                  onClick={() => setActive(s.key)}
                  data-testid={`step-nav-${s.key}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    active === s.key ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="flex-1 text-left">{s.label}</span>
                  <SIcon className={`w-4 h-4 ${STEP_STATE_TONE[s.status]}`} />
                </button>
              );
            })}
          </div>

          {/* Step panel */}
          <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-6">
            {active === "profile" && (
              <div data-testid="step-panel-profile">
                <h2 className="text-base font-bold mb-1">Company Profile</h2>
                <p className="text-sm text-slate-400 mb-5">Core identity and locale. Required fields drive the completion indicator.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <LabeledInput label="Legal company name" value={cfg.legal_name} onChange={(v) => setCfg({ ...cfg, legal_name: v })} testId="cfg-legal_name" />
                  <LabeledInput label="Trading name" value={cfg.trading_name} onChange={(v) => setCfg({ ...cfg, trading_name: v })} testId="cfg-trading_name" />
                  <LabeledInput label="Company number" value={cfg.company_number} onChange={(v) => setCfg({ ...cfg, company_number: v })} testId="cfg-company_number" />
                  <LabeledInput label="VAT number" value={cfg.vat_number} onChange={(v) => setCfg({ ...cfg, vat_number: v })} testId="cfg-vat_number" />
                  <LabeledInput label="Telephone" value={cfg.telephone} onChange={(v) => setCfg({ ...cfg, telephone: v })} testId="cfg-telephone" />
                  <LabeledInput label="General email" value={cfg.general_email} onChange={(v) => setCfg({ ...cfg, general_email: v })} testId="cfg-general_email" />
                  <LabeledInput label="Website" value={cfg.website} onChange={(v) => setCfg({ ...cfg, website: v })} testId="cfg-website" />
                  <LabeledInput label="Timezone" value={cfg.timezone} onChange={(v) => setCfg({ ...cfg, timezone: v })} testId="cfg-timezone" />
                  <LabeledInput label="Locale" value={cfg.locale} onChange={(v) => setCfg({ ...cfg, locale: v })} testId="cfg-locale" />
                  <LabeledInput label="Currency" value={cfg.currency} onChange={(v) => setCfg({ ...cfg, currency: v })} testId="cfg-currency" />
                </div>
                <div className="mt-4">
                  <label className={labelCls}>Address</label>
                  <textarea value={cfg.address || ""} onChange={(e) => setCfg({ ...cfg, address: e.target.value })} data-testid="cfg-address" rows={2} className={inputCls} />
                </div>
                <div className="mt-5 flex justify-end">
                  <button onClick={saveProfile} disabled={saving} data-testid="save-profile" className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold text-sm px-4 py-2"><Save className="w-4 h-4" /> Save profile</button>
                </div>
              </div>
            )}

            {active === "branding" && (
              <div data-testid="step-panel-branding">
                <h2 className="text-base font-bold mb-1">Branding</h2>
                <p className="text-sm text-slate-400 mb-5">Surfaces the existing company branding used across the app shell, customer-facing identity and PDF documents.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <LabeledInput label="Display name (customer-facing)" value={brand.display_name} onChange={(v) => setBrand({ ...brand, display_name: v })} testId="brand-display_name" />
                  <LabeledInput label="Logo URL" value={brand.logo_url} onChange={(v) => setBrand({ ...brand, logo_url: v })} testId="brand-logo_url" placeholder="https://…" />
                  <div>
                    <label className={labelCls}>Primary colour</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={brand.primary_color || "#2563eb"} onChange={(e) => setBrand({ ...brand, primary_color: e.target.value })} data-testid="brand-primary_color" className="h-9 w-12 rounded border border-slate-200 bg-white p-0.5" />
                      <input value={brand.primary_color || ""} onChange={(e) => setBrand({ ...brand, primary_color: e.target.value })} placeholder="#2563eb" className={inputCls} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Secondary / accent colour</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={brand.accent_color || "#6366f1"} onChange={(e) => setBrand({ ...brand, accent_color: e.target.value })} data-testid="brand-accent_color" className="h-9 w-12 rounded border border-slate-200 bg-white p-0.5" />
                      <input value={brand.accent_color || ""} onChange={(e) => setBrand({ ...brand, accent_color: e.target.value })} placeholder="#6366f1" className={inputCls} />
                    </div>
                  </div>
                </div>
                {brand.logo_url && <img src={brand.logo_url} alt="logo preview" className="mt-4 h-12 object-contain" onError={(e) => (e.target.style.display = "none")} />}
                <div className="mt-5 flex justify-end">
                  <button onClick={saveBranding} disabled={saving} data-testid="save-branding" className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold text-sm px-4 py-2"><Save className="w-4 h-4" /> Save branding</button>
                </div>
              </div>
            )}

            {active === "operating_model" && (
              <div data-testid="step-panel-operating_model">
                <h2 className="text-base font-bold mb-1">Operating Model</h2>
                <p className="text-sm text-slate-400 mb-5">Pick how this client operates. This only <span className="font-semibold text-slate-500">recommends</span> modules — it never locks them.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {(meta?.operating_models || []).map((o) => (
                    <button
                      key={o.key}
                      onClick={() => setOpModel(o.key)}
                      data-testid={`opmodel-${o.key}`}
                      className={`text-left rounded-xl border p-4 transition-all ${
                        opModel === o.key ? "border-blue-400 ring-2 ring-blue-500/20 bg-blue-50/40" : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="font-semibold text-sm text-slate-800">{o.label}</div>
                      <div className="text-[11px] text-slate-400 mt-1">{(meta?.operating_model_modules[o.key] || []).join(", ")}</div>
                    </button>
                  ))}
                </div>
                {opModel && (
                  <div className="mt-5 rounded-xl bg-slate-50 ring-1 ring-slate-200 p-4">
                    <div className="text-xs font-semibold text-slate-500 mb-2">Recommended modules</div>
                    <div className="flex flex-wrap gap-1.5">
                      {recommended.map((m) => (
                        <span key={m} className={`px-2 py-0.5 rounded text-[11px] font-medium ${modules.includes(m) ? "bg-emerald-100 text-emerald-700" : "bg-white text-slate-500 ring-1 ring-slate-200"}`}>{m}</span>
                      ))}
                    </div>
                    <button onClick={applyRecommendation} disabled={saving} data-testid="apply-recommendation" className="mt-3 text-xs font-semibold text-blue-700 hover:text-blue-800">Enable recommended modules →</button>
                  </div>
                )}
                <div className="mt-5 flex justify-end">
                  <button onClick={saveOpModel} disabled={saving || !opModel} data-testid="save-opmodel" className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold text-sm px-4 py-2"><Save className="w-4 h-4" /> Save operating model</button>
                </div>
              </div>
            )}

            {active === "modules" && (
              <div data-testid="step-panel-modules">
                <h2 className="text-base font-bold mb-1">Modules</h2>
                <p className="text-sm text-slate-400 mb-5">Enable, plan or disable modules. Uses the existing module catalogue and deny-by-default gating — changes apply immediately.</p>
                <div className="space-y-2">
                  {(meta?.all_modules || []).map((m) => {
                    const state = modules.includes(m) ? "enabled" : planned.includes(m) ? "planned" : "disabled";
                    const isRec = recommended.includes(m);
                    return (
                      <div key={m} data-testid={`module-row-${m}`} className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-700 capitalize">{m}</span>
                          {isRec && <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 text-[10px] font-semibold">Recommended</span>}
                        </div>
                        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
                          {["enabled", "planned", "disabled"].map((opt) => (
                            <button
                              key={opt}
                              onClick={() => toggleModule(m, opt)}
                              data-testid={`module-${m}-${opt}`}
                              className={`px-2.5 py-1 rounded-md text-[11px] font-semibold capitalize transition-colors ${
                                state === opt
                                  ? opt === "enabled" ? "bg-emerald-500 text-white" : opt === "planned" ? "bg-amber-500 text-white" : "bg-slate-400 text-white"
                                  : "text-slate-500 hover:text-slate-700"
                              }`}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {active === "users" && <UsersRolesPanel cid={id} onChange={load} />}
            {active === "data" && <DataMigrationPanel cid={id} onChange={load} />}
            {active === "communications" && <CommsPanel cid={id} onChange={load} />}
            {active === "customer_automations" && <CustomerAutomationsPanel cid={id} onChange={load} />}
            {active === "go_live" && <GoLiveReadinessPanel cid={id} onChange={load} onGoStep={setActive} />}
          </div>
        </div>
      </div>
    </div>
  );
}

const ROLE_BADGE = "px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-medium";
const USER_STATUS_STYLE = {
  Active: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  Invited: "bg-blue-50 text-blue-700 ring-blue-200",
  Pending: "bg-amber-50 text-amber-700 ring-amber-200",
  Disabled: "bg-slate-100 text-slate-400 ring-slate-200",
};

function RolePicker({ catalogue, selected, onToggle, testPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {catalogue.map((r) => {
        const on = selected.includes(r.key);
        return (
          <button
            key={r.key}
            type="button"
            onClick={() => onToggle(r.key)}
            data-testid={`${testPrefix}-role-${r.key}`}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold ring-1 transition-colors ${on ? "bg-blue-600 text-white ring-blue-600" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}
          >
            {r.label}
            {r.mobile && <Smartphone className="w-3 h-3 opacity-70" />}
            {r.recommended && !on && <span className="text-indigo-500">★</span>}
          </button>
        );
      })}
    </div>
  );
}

function UsersRolesPanel({ cid, onChange }) {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [lookup, setLookup] = useState(null);
  const [newRoles, setNewRoles] = useState([]);
  const [editing, setEditing] = useState(null); // {uid, roles}
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/platform/companies/${cid}/users`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  const doLookup = async () => {
    const e = email.trim().toLowerCase();
    if (!e) { setLookup(null); return; }
    try { const r = await api.post(`/platform/companies/${cid}/users/lookup`, { email: e }); setLookup(r.data); }
    catch { setLookup(null); }
  };
  const refresh = () => { load(); onChange && onChange(); };
  const add = async () => {
    const e = email.trim().toLowerCase();
    if (!e) { toast.error("Enter an email"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/platform/companies/${cid}/members`, { email: e, tenant_roles: newRoles });
      toast.success(r.data.invited ? "User invited & added" : "Existing user added to company");
      setEmail(""); setLookup(null); setNewRoles([]); refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  const saveRoles = async (uid, roles) => {
    setBusy(true);
    try { await api.patch(`/platform/companies/${cid}/members/${uid}`, { tenant_roles: roles }); toast.success("Roles updated"); setEditing(null); refresh(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  const setStatus = async (uid, status) => {
    setBusy(true);
    try { await api.patch(`/platform/companies/${cid}/members/${uid}`, { status }); toast.success(status === "active" ? "Enabled" : "Disabled"); refresh(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  const remove = async (uid) => {
    setBusy(true);
    try { await api.delete(`/platform/companies/${cid}/members/${uid}`); toast.success("Membership removed (global account kept)"); refresh(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  const review = async (val) => {
    setBusy(true);
    try { await api.post(`/platform/companies/${cid}/users/review-complete`, { reviewed: val }); toast.success(val ? "Marked complete" : "Review reopened"); refresh(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  if (!data) return <div data-testid="step-panel-users" className="text-sm text-slate-400">Loading users…</div>;
  const cat = (data.role_catalogue || []);
  const hasSuper = data.summary.super_admins > 0;

  return (
    <div data-testid="step-panel-users">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
        <div>
          <h2 className="text-base font-bold">Users &amp; Roles</h2>
          <p className="text-sm text-slate-400">
            {data.summary.total} users · {data.summary.super_admins} Super Admin{data.summary.super_admins === 1 ? "" : "s"} · {data.summary.roles_in_use.length} roles in use
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!hasSuper
            ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600" data-testid="users-needs-super"><AlertTriangle className="w-4 h-4" /> Needs attention — no Super Admin</span>
            : data.summary.reviewed
              ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid="users-complete"><CheckCircle2 className="w-4 h-4" /> Complete</span>
              : <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400">Review pending</span>}
          <button onClick={() => review(!data.summary.reviewed)} disabled={busy || !hasSuper} data-testid="users-review-toggle"
            className="rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-xs font-semibold px-3 py-1.5">
            {data.summary.reviewed ? "Reopen review" : "Mark review complete"}
          </button>
        </div>
      </div>
      {!data.department_supported && <p className="text-[11px] text-slate-400 mb-4">Department/team assignment is deferred — no per-member department model exists yet.</p>}

      {/* Add user */}
      <div className="rounded-xl ring-1 ring-slate-200 bg-slate-50/60 p-4 mb-5">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Add user</div>
        <div className="flex flex-col sm:flex-row gap-2 mb-2">
          <input value={email} onChange={(e) => { setEmail(e.target.value); setLookup(null); }} onBlur={doLookup}
            placeholder="person@company.co.uk" data-testid="adduser-email" className={inputCls + " sm:max-w-xs"} />
          <button onClick={add} disabled={busy} data-testid="adduser-submit"
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold text-sm px-4 py-2 whitespace-nowrap">
            <UserPlus className="w-4 h-4" /> {lookup?.exists ? "Add existing user" : "Add / invite"}
          </button>
        </div>
        {lookup && (
          <div className="text-xs mb-2" data-testid="adduser-lookup">
            {lookup.exists
              ? (lookup.already_member
                ? <span className="text-amber-600">Already a member of this company ({(lookup.tenant_roles || []).join(", ") || "no roles"}). Saving updates their roles.</span>
                : <span className="text-emerald-600">Existing TedOps user “{lookup.name}” — a new membership will be added (no duplicate account).</span>)
              : <span className="text-slate-500">New email — the user will be invited through the existing flow.</span>}
          </div>
        )}
        <RolePicker catalogue={cat} selected={newRoles} onToggle={(k) => setNewRoles((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k])} testPrefix="adduser" />
        {data.recommended_roles.length > 0 && <p className="text-[11px] text-indigo-500 mt-2">★ Recommended for enabled modules: {data.recommended_roles.join(", ")} (advisory)</p>}
      </div>

      {/* User list */}
      <div className="rounded-xl ring-1 ring-slate-200 bg-white divide-y divide-slate-100">
        {data.users.map((u) => (
          <div key={u.user_id} data-testid={`user-row-${u.user_id}`} className="px-4 py-3">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5 truncate">
                  {u.name || u.email || "Unknown"}
                  {u.is_super_admin && <ShieldCheck className="w-3.5 h-3.5 text-violet-500" title="Company Super Admin" />}
                  {u.mobile_access && <Smartphone className="w-3.5 h-3.5 text-slate-400" title="Mobile/field access" />}
                </div>
                <div className="text-xs text-slate-400 truncate">{u.email || "—"}</div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${USER_STATUS_STYLE[u.status]}`} data-testid={`user-status-${u.user_id}`}>{u.status}</span>
              <div className="hidden sm:flex flex-wrap gap-1 max-w-[220px]">
                {u.role_labels.length ? u.role_labels.map((r, i) => <span key={i} className={ROLE_BADGE}>{r}</span>) : <span className="text-xs text-slate-300">No roles</span>}
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => setEditing(editing?.uid === u.user_id ? null : { uid: u.user_id, roles: [...u.tenant_roles] })} data-testid={`user-editroles-${u.user_id}`}
                  className="rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-semibold px-2.5 py-1.5">Change role</button>
                {u.membership_status === "active"
                  ? <button onClick={() => setStatus(u.user_id, "suspended")} disabled={busy} data-testid={`user-disable-${u.user_id}`} className="rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 text-xs font-semibold px-2.5 py-1.5">Disable</button>
                  : <button onClick={() => setStatus(u.user_id, "active")} disabled={busy} data-testid={`user-enable-${u.user_id}`} className="rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold px-2.5 py-1.5">Enable</button>}
                <button onClick={() => remove(u.user_id)} disabled={busy} data-testid={`user-remove-${u.user_id}`} className="rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 text-xs font-semibold px-2 py-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            {editing?.uid === u.user_id && (
              <div className="mt-3 rounded-lg bg-slate-50 ring-1 ring-slate-200 p-3" data-testid={`user-roleeditor-${u.user_id}`}>
                <RolePicker catalogue={cat} selected={editing.roles} onToggle={(k) => setEditing((p) => ({ ...p, roles: p.roles.includes(k) ? p.roles.filter((x) => x !== k) : [...p.roles, k] }))} testPrefix={`edit-${u.user_id}`} />
                <div className="flex justify-end gap-2 mt-3">
                  <button onClick={() => setEditing(null)} className="text-xs font-semibold text-slate-500 px-2 py-1.5">Cancel</button>
                  <button onClick={() => saveRoles(u.user_id, editing.roles)} disabled={busy} data-testid={`user-saveroles-${u.user_id}`} className="inline-flex items-center gap-1 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5"><Save className="w-3.5 h-3.5" /> Save roles</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


const DATA_STRATEGIES = [
  { key: "start_clean", label: "Start Clean", desc: "No operational data imported — begin with an empty tenant." },
  { key: "existing", label: "Use Existing TedOps Data", desc: "This company already has data inside TedOps — use it, do not duplicate." },
  { key: "import", label: "Import Existing Data", desc: "Prepare & import data from an external CSV / spreadsheet." },
];
const ROW_STATUS_STYLE = {
  valid: "bg-emerald-50 text-emerald-700", warning: "bg-amber-50 text-amber-700",
  duplicate: "bg-blue-50 text-blue-700", rejected: "bg-rose-50 text-rose-700",
};

function DataMigrationPanel({ cid, onChange }) {
  const [data, setData] = useState(null);
  const [entity, setEntity] = useState("contacts");
  const [batch, setBatch] = useState(null);
  const [detail, setDetail] = useState(null);
  const [mapping, setMapping] = useState({});
  const [dupStrategy, setDupStrategy] = useState("skip");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/platform/companies/${cid}/data`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  const refresh = () => { load(); onChange && onChange(); };
  const setStrategy = async (strategy, reviewed) => {
    setBusy(true);
    try { await api.post(`/platform/companies/${cid}/data-strategy`, { strategy, reviewed }); toast.success("Saved"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.onerror = rej; r.readAsDataURL(file); });
      const ft = file.name.toLowerCase().endsWith(".xlsx") ? "xlsx" : "csv";
      const r = await api.post(`/platform/companies/${cid}/import/parse`, { entity, filename: file.name, file_type: ft, content_b64: b64 });
      setBatch(r.data.batch); setDetail({ fields: r.data.fields, sample: [], batch: r.data.batch });
      const auto = {};
      r.data.fields.forEach((f) => {
        const hit = r.data.batch.columns.find((c) => c.toLowerCase().replace(/[^a-z0-9]/g, "") === f.key.toLowerCase().replace(/[^a-z0-9]/g, "") || c.toLowerCase() === f.label.toLowerCase());
        if (hit) auto[f.key] = hit;
      });
      setMapping(auto);
      toast.success(`Parsed ${r.data.batch.rows_received} rows`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const saveMapAndValidate = async () => {
    setBusy(true);
    try {
      await api.post(`/platform/companies/${cid}/import/${batch.id}/mapping`, { mapping });
      await api.post(`/platform/companies/${cid}/import/${batch.id}/validate`);
      const r = await api.get(`/platform/companies/${cid}/import/${batch.id}`);
      setDetail(r.data); setBatch(r.data.batch);
      toast.success("Validated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/platform/companies/${cid}/import/${batch.id}/apply`, { duplicate_strategy: dupStrategy, confirm: true });
      toast.success(`Applied — ${r.data.applied_counts.created} created, ${r.data.applied_counts.updated} updated`);
      setBatch(null); setDetail(null); setMapping({}); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (!data) return <div data-testid="step-panel-data" className="text-sm text-slate-400">Loading…</div>;
  const supported = data.entities.filter((e) => e.supported);
  const unsupported = data.entities.filter((e) => !e.supported);
  const fields = (supported.find((e) => e.key === entity) || {}).fields || [];
  const counts = batch?.counts || {};

  return (
    <div data-testid="step-panel-data">
      <h2 className="text-base font-bold mb-1">Data &amp; Migration</h2>
      <p className="text-sm text-slate-400 mb-5">Choose how this client starts. Imports are staged and never written to live data until you approve.</p>

      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        {DATA_STRATEGIES.map((s) => (
          <button key={s.key} onClick={() => setStrategy(s.key, false)} data-testid={`data-strategy-${s.key}`}
            className={`text-left rounded-xl border p-4 transition-all ${data.strategy === s.key ? "border-blue-400 ring-2 ring-blue-500/20 bg-blue-50/40" : "border-slate-200 hover:border-slate-300"}`}>
            <div className="font-semibold text-sm text-slate-800">{s.label}</div>
            <div className="text-[11px] text-slate-400 mt-1">{s.desc}</div>
          </button>
        ))}
      </div>

      {(data.strategy === "start_clean" || data.strategy === "existing") && (
        <div className="flex items-center justify-between rounded-xl bg-slate-50 ring-1 ring-slate-200 p-4 mb-5">
          <span className="text-sm text-slate-600">{data.reviewed ? "This step is marked complete." : "Confirm this choice to complete the step."}</span>
          <button onClick={() => setStrategy(data.strategy, !data.reviewed)} disabled={busy} data-testid="data-review-toggle"
            className="rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5">
            {data.reviewed ? "Reopen" : "Mark reviewed & complete"}
          </button>
        </div>
      )}

      {data.strategy === "import" && (
        <div className="rounded-xl ring-1 ring-slate-200 bg-white p-5 mb-5">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <label className="text-xs text-slate-500">Entity
              <select value={entity} onChange={(e) => { setEntity(e.target.value); setBatch(null); setDetail(null); }} data-testid="import-entity" className={`ml-2 ${sel}`}>
                {supported.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
              </select>
            </label>
            <label className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-3 py-2 cursor-pointer" data-testid="import-upload-label">
              <Upload className="w-4 h-4" /> Upload CSV / XLSX
              <input type="file" accept=".csv,.xlsx" onChange={onFile} className="hidden" data-testid="import-file-input" />
            </label>
            {batch && <span className="text-xs text-slate-400">{batch.filename} · {batch.rows_received} rows · <span className="font-semibold capitalize">{batch.status}</span></span>}
          </div>

          {batch && (
            <>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Map fields</div>
              <div className="grid sm:grid-cols-2 gap-2 mb-4">
                {fields.map((f) => (
                  <label key={f.key} className="flex items-center justify-between gap-2 text-xs text-slate-600 border border-slate-100 rounded-lg px-3 py-1.5">
                    <span>{f.label}{f.required && <span className="text-rose-500"> *</span>}</span>
                    <select value={mapping[f.key] || ""} onChange={(e) => setMapping((p) => ({ ...p, [f.key]: e.target.value }))} data-testid={`map-${f.key}`} className={sel}>
                      <option value="">— unmapped —</option>
                      {batch.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                ))}
              </div>
              <button onClick={saveMapAndValidate} disabled={busy} data-testid="import-validate" className="rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold px-4 py-2 mb-4">Validate</button>

              {batch.status === "validated" && (
                <>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Preview</div>
                  <div className="flex flex-wrap gap-2 mb-3" data-testid="import-counts">
                    {["valid", "warning", "duplicate", "rejected"].map((k) => (
                      <span key={k} className={`px-2.5 py-1 rounded-lg text-xs font-semibold capitalize ${ROW_STATUS_STYLE[k]}`}>{counts[k] || 0} {k}</span>
                    ))}
                  </div>
                  {detail?.sample?.length > 0 && (
                    <div className="max-h-48 overflow-y-auto rounded-lg ring-1 ring-slate-200 mb-3 text-xs">
                      {detail.sample.slice(0, 12).map((r) => (
                        <div key={r.idx} className="flex items-center gap-2 px-3 py-1.5 border-b border-slate-50 last:border-0">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold capitalize ${ROW_STATUS_STYLE[r.status]}`}>{r.status}</span>
                          <span className="text-slate-600 truncate flex-1">{Object.values(r.business || {}).join(" · ") || "—"}</span>
                          {r.issues?.length > 0 && <span className="text-rose-500 truncate max-w-[40%]" title={r.issues.join(", ")}>{r.issues[0]}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="text-xs text-slate-500">Duplicates
                      <select value={dupStrategy} onChange={(e) => setDupStrategy(e.target.value)} data-testid="import-dup-strategy" className={`ml-2 ${sel}`}>
                        <option value="skip">Skip</option>
                        <option value="update">Update existing</option>
                      </select>
                    </label>
                    <button onClick={apply} disabled={busy} data-testid="import-apply" className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2">
                      <CheckCircle className="w-4 h-4" /> Approve &amp; Apply import
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {data.batches.length > 0 && (
        <div className="mb-5">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Import history</div>
          <div className="rounded-xl ring-1 ring-slate-200 bg-white divide-y divide-slate-100 text-xs">
            {data.batches.map((b) => (
              <div key={b.id} data-testid={`batch-row-${b.id}`} className="flex items-center justify-between px-4 py-2">
                <span className="text-slate-600">{b.filename} · <span className="capitalize">{b.entity}</span></span>
                <span className="flex items-center gap-2">
                  <span className="capitalize text-slate-400">{b.status}</span>
                  {b.applied_counts && <span className="text-emerald-600 font-semibold">+{b.applied_counts.created} created</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-4 text-xs text-slate-500">
        <div className="font-semibold text-slate-600 mb-1">Not yet supported</div>
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="data-unsupported">
          {unsupported.map((e) => <span key={e.key} className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-500">{e.label}</span>)}
        </div>
        <p>{data.attachments_note}</p>
      </div>
    </div>
  );
}

const CHANNEL_ICON = { in_app: Bell, email: Mail, push: Phone, sms: MessageSquare, whatsapp: MessageSquare };
const CH_STATUS_STYLE = {
  ready: "text-emerald-600", disabled: "text-slate-400", needs_configuration: "text-amber-600", unavailable: "text-slate-300",
};
const ROUTE_STATUS_STYLE = {
  ready: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  attention: "bg-amber-50 text-amber-700 ring-amber-200",
  incomplete: "bg-slate-100 text-slate-500 ring-slate-200",
  disabled: "bg-slate-100 text-slate-400 ring-slate-200",
};

function CommsPanel({ cid, onChange }) {
  const [data, setData] = useState(null);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(null);
  const [extEmail, setExtEmail] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/platform/companies/${cid}/communications`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get(`/platform/companies/${cid}/users`).then((r) => { setRoles(r.data.role_catalogue || []); setUsers(r.data.users || []); }).catch(() => {});
    /* eslint-disable-next-line */
  }, [cid]);

  const refresh = () => { load(); onChange && onChange(); };
  const act = async (fn, msg) => { setBusy(true); try { await fn(); if (msg) toast.success(msg); refresh(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setBusy(false); } };
  const setChannel = (ch, patch) => act(() => api.put(`/platform/companies/${cid}/communications/${ch}`, patch), "Channel updated");
  const saveRoute = (ev, patch) => act(() => api.put(`/platform/companies/${cid}/routes/${ev}`, patch), "Route saved");
  const review = (val) => act(() => api.post(`/platform/companies/${cid}/communications/review`, { reviewed: val }), val ? "Marked complete" : "Reopened");
  const runTest = async (ev, confirm) => {
    setBusy(true);
    try { const r = await api.post(`/platform/companies/${cid}/routes/${ev}/test`, { confirm }); setTestResult({ ev, ...r.data }); if (confirm) toast.success(`Test sent to ${r.data.preview.count} recipient(s)`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (!data) return <div data-testid="step-panel-communications" className="text-sm text-slate-400">Loading…</div>;
  const chmap = Object.fromEntries(data.channels.map((c) => [c.key, c]));

  return (
    <div data-testid="step-panel-communications">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div><h2 className="text-base font-bold">Communications &amp; Routing</h2>
          <p className="text-sm text-slate-400">Configure channels and route internal operational events to the right people.</p></div>
        <div className="flex items-center gap-2">
          {data.reviewed ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid="comms-complete"><CheckCircle className="w-4 h-4" /> Complete</span>
            : data.has_attention ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600" data-testid="comms-attention"><AlertTriangle className="w-4 h-4" /> Needs attention</span>
            : <span className="text-xs text-slate-400">Review pending</span>}
          <button onClick={() => review(!data.reviewed)} disabled={busy} data-testid="comms-review-toggle" className="rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5">{data.reviewed ? "Reopen" : "Mark reviewed & complete"}</button>
        </div>
      </div>

      {/* Channels */}
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Channels</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-6">
        {data.channels.map((c) => {
          const Icon = CHANNEL_ICON[c.key] || Bell;
          return (
            <div key={c.key} data-testid={`channel-${c.key}`} className="rounded-xl ring-1 ring-slate-200 bg-white p-3 flex items-center gap-3">
              <Icon className="w-4 h-4 text-slate-500" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800">{c.label}</div>
                <div className={`text-[11px] font-medium capitalize ${CH_STATUS_STYLE[c.status]}`}>{c.supported ? c.status.replace("_", " ") : "Unavailable / Planned"}</div>
              </div>
              {c.supported ? (
                <button onClick={() => setChannel(c.key, { enabled: !c.enabled })} disabled={busy || !c.configured} data-testid={`channel-toggle-${c.key}`}
                  className={`text-[11px] font-semibold px-2.5 py-1 rounded-lg ${c.enabled ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-500"} disabled:opacity-40`}>{c.enabled ? "On" : "Off"}</button>
              ) : <span className="text-[10px] text-slate-300 font-semibold uppercase">Planned</span>}
            </div>
          );
        })}
      </div>

      {/* Events / routing */}
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Notification routing</div>
      <div className="rounded-xl ring-1 ring-slate-200 bg-white divide-y divide-slate-100">
        {data.events.map((ev) => {
          const rt = ev.route;
          return (
            <div key={ev.key} data-testid={`route-${ev.key}`}>
              <button onClick={() => { setOpen(open === ev.key ? null : ev.key); setExtEmail(""); setTestResult(null); }} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left">
                <span className="flex-1 min-w-0 text-sm font-medium text-slate-800 truncate">{ev.label}{ev.trigger_available ? <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Live</span> : <span className="ml-2 text-[10px] text-slate-400">live trigger: Not Yet Available</span>}</span>
                <span className="hidden sm:flex gap-1">{rt.channels.map((c) => <span key={c} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] capitalize">{c.replace("_", "-")}</span>)}</span>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${ROUTE_STATUS_STYLE[rt.status]}`} data-testid={`route-status-${ev.key}`}>{rt.status === "attention" ? "Needs attention" : rt.status}</span>
              </button>
              {open === ev.key && (
                <div className="px-5 pb-4 pt-1 bg-slate-50/50 space-y-3" data-testid={`route-editor-${ev.key}`}>
                  <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={rt.enabled} onChange={(e) => saveRoute(ev.key, { enabled: e.target.checked })} data-testid={`route-enabled-${ev.key}`} /> Route enabled</label>
                  <div>
                    <div className="text-[11px] font-semibold text-slate-500 mb-1">Channels</div>
                    <div className="flex flex-wrap gap-1.5">
                      {data.channels.filter((c) => c.supported).map((c) => {
                        const on = rt.channels.includes(c.key);
                        return <button key={c.key} onClick={() => saveRoute(ev.key, { channels: on ? rt.channels.filter((x) => x !== c.key) : [...rt.channels, c.key] })} data-testid={`route-${ev.key}-ch-${c.key}`}
                          className={`px-2 py-1 rounded-lg text-[11px] font-semibold ring-1 ${on ? (c.configured ? "bg-blue-600 text-white ring-blue-600" : "bg-amber-500 text-white ring-amber-500") : "bg-white text-slate-600 ring-slate-200"}`}>{c.label}{on && !c.configured && " ⚠"}</button>;
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold text-slate-500 mb-1">Recipients</div>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {rt.recipients.map((r, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-200 text-slate-600 text-[11px]">
                          {r.type === "role" ? `Role: ${r.value}` : r.type === "user" ? `User` : r.value}
                          <button onClick={() => saveRoute(ev.key, { recipients: rt.recipients.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-rose-500"><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                      {rt.recipients.length === 0 && <span className="text-[11px] text-slate-300">No recipients</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <select onChange={(e) => { if (e.target.value) { saveRoute(ev.key, { recipients: [...rt.recipients, { type: "role", value: e.target.value }] }); e.target.value = ""; } }} data-testid={`route-${ev.key}-add-role`} className={sel}>
                        <option value="">+ Add role…</option>
                        {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                      </select>
                      <select onChange={(e) => { if (e.target.value) { saveRoute(ev.key, { recipients: [...rt.recipients, { type: "user", value: e.target.value }] }); e.target.value = ""; } }} data-testid={`route-${ev.key}-add-user`} className={sel}>
                        <option value="">+ Add user…</option>
                        {users.filter((u) => u.email).map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
                      </select>
                      <input value={extEmail} onChange={(e) => setExtEmail(e.target.value)} placeholder="external@email" data-testid={`route-${ev.key}-ext`} className={inputCls + " max-w-[180px]"} />
                      <button onClick={() => { if (extEmail.trim()) { saveRoute(ev.key, { recipients: [...rt.recipients, { type: "external_email", value: extEmail.trim() }] }); setExtEmail(""); } }} className="text-xs font-semibold text-blue-600">Add email</button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <button onClick={() => runTest(ev.key, false)} disabled={busy} data-testid={`route-preview-${ev.key}`} className="rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-semibold px-3 py-1.5">Preview recipients</button>
                    {testResult?.ev === ev.key && testResult.preview && (
                      <button onClick={() => runTest(ev.key, true)} disabled={busy || testResult.preview.count === 0} data-testid={`route-test-${ev.key}`} className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white text-xs font-semibold px-3 py-1.5"><Send className="w-3.5 h-3.5" /> Send test</button>
                    )}
                  </div>
                  {testResult?.ev === ev.key && (
                    <div className="text-[11px] text-slate-500 rounded-lg bg-white ring-1 ring-slate-200 p-2" data-testid={`route-testresult-${ev.key}`}>
                      Would notify {testResult.preview.count}: {testResult.preview.would_notify.map((p) => p.name || p.email).join(", ") || "—"}
                      {testResult.preview.skipped_channels.length > 0 && <span className="text-amber-600"> · skipped (unconfigured): {testResult.preview.skipped_channels.join(", ")}</span>}
                      {testResult.sent && <span className="text-emerald-600"> · sent: {JSON.stringify(testResult.delivered)}</span>}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

